import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { FormikProps } from "formik"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { CreateUpdateTemplateFormValues } from "../supplierPosSettingsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { PosSettingQuery, TemplateDetailsResponse, getTemplateDetails, updateTemplate } from "../supplierPosSettingsApi"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { SUPPLIER_ADMIN_POS_SETTINGS_ROUTE, SUPPLIER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateTemplateForm } from "../components/CreateUpdateTemplateForm"
import { prepareInitialValues } from "../helpers/prepareInitialValues"

const selectTemplate = (data: TemplateDetailsResponse) => data.template

const Template = forwardRef<FormikProps<CreateUpdateTemplateFormValues>>((_props, ref) => {
    const { templateId } = useParams()
    const navigate = useNavigate()

    const { data: template } = useApiQuery(getTemplateDetails, {
        queryName: PosSettingQuery.TEMPLATE_DETAILS,
        dependencies: {
            templateId: Number(templateId) ?? 0,
        },
        enabled: Boolean(templateId),
        select: selectTemplate,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutate: callUpdateTemplate } = useApiMutation(updateTemplate, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PosSettingQuery.TEMPLATES_LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(PosSettingQuery.TEMPLATE_DETAILS, { templateId: params.templateId }),
            })
            navigate(SUPPLIER_ADMIN_POS_SETTINGS_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateTemplateForm
                ref={ref}
                initialValues={prepareInitialValues(template)}
                onSubmit={(data) => {
                    templateId && callUpdateTemplate({ template: data, templateId: Number(templateId) })
                }}
            />
        </>
    )
})

export function EditTemplatePage() {
    const templateFormRef = useRef<FormikProps<CreateUpdateTemplateFormValues>>(null)

    const handleSaveClick = () => {
        if (templateFormRef.current) {
            templateFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="supplier-admin-pos-settings-edit-template-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_ADMIN_POS_SETTINGS_EDIT}
                    title="Edit template"
                    rightElement={
                        <LocalizedStrict id="supplier-admin-pos-settings-edit-submit-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-settings">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-pos-settings">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_POS_SETTINGS_ROUTE}>POS settings</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-pos-settings-template-edit">
                            <Breadcrumb>Edit template</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Template ref={templateFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
